/**
 * Chat WebSocket Hook (Socket.IO)
 * Manages Socket.IO connection for real-time chat streaming
 * Implements 2025 best practices: auto-reconnect, exponential backoff, connection state management
 * Production-grade error handling and resilient connection management
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getApiConfig } from '../../../../lib/api/config';
import type { ChatStreamChunk, StructuredSocketError, SocketErrorType, ChatSuggestion, ChatWebSocketMessage, StreamResumeData } from '../types/chat.types';

interface UseChatWebSocketOptions {
  onChunk?: (chunk: ChatStreamChunk) => void;
  onTyping?: () => void;
  onComplete?: (messageId: string | null, metadata?: { isOffTopic?: boolean; suggestions?: ChatSuggestion[] }) => void;
  onError?: (error: string, errorType?: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  /** Called when backend sends buffered stream content on reconnection */
  onStreamResume?: (data: StreamResumeData) => void;
  enabled?: boolean;
  accessToken?: string;
  currentConversationId?: string | null; // For rejoin on visibility
  onRejoinNeeded?: () => void; // Callback when conversation needs to be rejoined
  onConversationNotFound?: (conversationId: string) => void; // Callback when conversation doesn't exist (for cleanup)
}

/**
 * Pending operation queue for when socket is disconnected
 */
interface PendingOperation {
  type: 'join_conversation';
  conversationId: string;
  guestId?: string | null;
}

/**
 * Socket.IO hook with 2025 best practices:
 * - Shared socket instance with auto-reconnect and exponential backoff
 * - Pre-send connection check (triggers reconnect if needed)
 * - Connection state management (isConnected, isConnecting, reconnectAttempts)
 * - Internet-aware reconnection (pauses when offline, resumes when online)
 * - Operation queue for disconnected state
 */
export const useChatWebSocket = ({
  onChunk,
  onTyping,
  onComplete,
  onError,
  onOpen,
  onClose,
  onStreamResume,
  enabled = true,
  accessToken,
  currentConversationId,
  onRejoinNeeded,
  onConversationNotFound
}: UseChatWebSocketOptions): {
  joinConversation: (conversationId: string, guestId?: string | null) => void;
  disconnect: () => void;
  isConnected: boolean;
  isConnecting: boolean;
  reconnect: () => void;
  clearStalePendingOperations: (staleConversationId: string) => void;
  updatePendingOperationsConversationId: (oldId: string, newId: string) => void;
} => {
  const socketRef = useRef<Socket | null>(null);
  const joinedConversationsRef = useRef<Set<string>>(new Set());
  const joinRetryCountRef = useRef<Map<string, number>>(new Map());
  const joinRetryTimeoutRef = useRef<Map<string, number>>(new Map());
  const lastAccessTokenRef = useRef<string | undefined>(accessToken);
  const eventHandlersRegisteredRef = useRef<boolean>(false);
  const pendingOperationsRef = useRef<PendingOperation[]>([]);
  const reconnectAttemptsRef = useRef<number>(0);
  const isOnlineRef = useRef<boolean>(navigator.onLine);
  const isMountedRef = useRef<boolean>(true);
  const activeJoinOperationsRef = useRef<Set<string>>(new Set());
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const lastRefreshTimeRef = useRef<number>(0); // Track last state refresh time
  const visibilityDebounceTimeoutRef = useRef<number | null>(null); // Debounce visibility changes

  // Refs for latest values - avoid stale closure in socket handlers (registered once)
  const latestConversationIdRef = useRef<string | null>(currentConversationId ?? null);
  const latestOnChunkRef = useRef(onChunk);
  const latestOnTypingRef = useRef(onTyping);
  const latestOnCompleteRef = useRef(onComplete);
  const latestOnErrorRef = useRef(onError);
  const latestOnStreamResumeRef = useRef(onStreamResume);
  const latestOnConversationNotFoundRef = useRef(onConversationNotFound);

  useEffect(() => {
    latestConversationIdRef.current = currentConversationId ?? null;
    latestOnChunkRef.current = onChunk;
    latestOnTypingRef.current = onTyping;
    latestOnCompleteRef.current = onComplete;
    latestOnErrorRef.current = onError;
    latestOnStreamResumeRef.current = onStreamResume;
    latestOnConversationNotFoundRef.current = onConversationNotFound;
  }, [currentConversationId, onChunk, onTyping, onComplete, onError, onStreamResume, onConversationNotFound]);

  const MAX_RETRY_ATTEMPTS = 3;
  const MIN_REFRESH_INTERVAL_MS = 3000; // Minimum 3 seconds between refreshes
  
  // Exponential backoff configuration: 1s → 2s → 4s → 8s → 16s (max)
  const RECONNECT_DELAY_MIN = 1000;
  const RECONNECT_DELAY_MAX = 16000;

  /**
   * Clear stale operations for a specific conversationId from pending queue
   * Used when conversationId changes to prevent joining with stale IDs
   */
  const clearStalePendingOperations = useCallback((staleConversationId: string): void => {
    pendingOperationsRef.current = pendingOperationsRef.current.filter(
      op => !(op.type === 'join_conversation' && op.conversationId === staleConversationId)
    );
  }, []);

  /**
   * Update conversationId in pending operations queue
   * Used when conversationId changes to update queued operations with new ID
   */
  const updatePendingOperationsConversationId = useCallback((oldId: string, newId: string): void => {
    pendingOperationsRef.current = pendingOperationsRef.current.map(op => {
      if (op.type === 'join_conversation' && op.conversationId === oldId) {
        return { ...op, conversationId: newId };
      }
      return op;
    });
  }, []);

  /**
   * Process pending operations queue when connection is established
   * Ensures connection is stable before processing
   * CRITICAL: Only processes operations with current conversationId to prevent stale joins
   */
  const processPendingOperations = useCallback((): void => {
    if (pendingOperationsRef.current.length === 0) return;
    if (!socketRef.current?.connected) return;
    if (!isMountedRef.current) return;
    
    // Wait a small delay to ensure connection is fully stable
    setTimeout(() => {
      if (!socketRef.current?.connected || !isMountedRef.current) {
        // Connection lost or component unmounted - keep operations queued
        return;
      }
      
      const operations = [...pendingOperationsRef.current];
      pendingOperationsRef.current = [];
      
      operations.forEach(op => {
        if (op.type === 'join_conversation') {
          // CRITICAL: Only process if conversationId matches current conversationId
          // This prevents joining with stale IDs that were queued before conversationId changed
          if (latestConversationIdRef.current && op.conversationId !== latestConversationIdRef.current) {
            // Stale operation - conversationId has changed, skip it
            console.warn(`Skipping stale join operation for conversation ${op.conversationId} - current is ${latestConversationIdRef.current}`);
            return;
          }
          
          // Only process if not already joined and component is still mounted
          if (!joinedConversationsRef.current.has(op.conversationId) && isMountedRef.current && socketRef.current) {
            try {
              socketRef.current.emit('join_conversation', {
                conversationId: op.conversationId,
                ...(op.guestId !== undefined ? { guestId: op.guestId } : {})
              });
              // Mark as active operation
              activeJoinOperationsRef.current.add(op.conversationId);
            } catch (error) {
              console.error('Failed to emit join_conversation from pending queue:', error);
              // Re-queue if failed
              pendingOperationsRef.current.push(op);
            }
          }
        }
      });
    }, 100); // Small delay to ensure connection is stable
  }, []);

  /**
   * Register event handlers on socket (only once)
   * Implements 2025 best practices: quiet retry, state management, operation queue
   */
  const registerEventHandlers = useCallback((socket: Socket): void => {
    if (eventHandlersRegisteredRef.current || !socket) return;

    // Connection opened - clear state, process pending operations
    socket.on('connect', (): void => {
      if (!isMountedRef.current) return;
      
      reconnectAttemptsRef.current = 0;
      setIsConnected(true);
      setIsConnecting(false);
      joinRetryCountRef.current.clear();
      
      // Process any pending operations after a brief delay to ensure connection is stable
      // This prevents race conditions where emit happens before connection is fully ready
      setTimeout(() => {
        if (socketRef.current?.connected && isMountedRef.current) {
          processPendingOperations();
        }
      }, 150);
      
      onOpen?.();
      // NOTE: Do NOT join conversation here - must wait for HTTP POST to create it
    });

    // Connection closed - start auto-reconnect if online
    socket.on('disconnect', (reason: string): void => {
      if (!isMountedRef.current) return;
      
      setIsConnected(false);
      setIsConnecting(false);
      
      // Don't clear joined conversations on disconnect - they'll be re-joined on reconnect
      // Only clear if it's a forced disconnect (component unmounting)
      if (reason === 'io client disconnect') {
        joinedConversationsRef.current.clear();
        activeJoinOperationsRef.current.clear();
      }
      
      // Clear retry timeouts on disconnect
      joinRetryTimeoutRef.current.forEach(timeout => window.clearTimeout(timeout));
      joinRetryTimeoutRef.current.clear();
      
      // Only log unexpected disconnects (not normal close events)
      if (reason !== 'io client disconnect' && reason !== 'io server disconnect') {
        // Quiet retry - don't spam user with errors
        // Socket.IO will auto-reconnect in background
        console.log('🔐 Chat Socket: Disconnected, reason:', reason, '- will auto-reconnect');
      }
      
      onClose?.();
    });

    // Connection error - retry with exponential backoff (quietly in background)
    socket.on('connect_error', (error: Error): void => {
      setIsConnecting(true);
      reconnectAttemptsRef.current += 1;
      
      // Only log if it's a persistent issue (after multiple attempts)
      if (reconnectAttemptsRef.current > 3) {
        console.warn('Chat Socket.IO connection error (retrying in background):', error.message);
      }
      
      // Don't spam user with errors - auto-reconnect handles it
      // Only notify on persistent failures (after 10+ attempts)
      if (reconnectAttemptsRef.current > 10) {
        latestOnErrorRef.current?.('Unable to connect to chat service. Please check your internet connection and try again.', 'connection_error');
      }
      
      // If connection fails after many attempts and we're not online, stop trying
      if (reconnectAttemptsRef.current > 20 && !isOnlineRef.current) {
        setIsConnecting(false);
        console.warn('Chat Socket.IO: Stopping reconnection attempts - device appears offline');
      }
    });

    // Server error events - handle with retry logic
    socket.on('error', (data: { type: string; message: string; retryAfter?: number }): void => {
      console.error('Chat Socket.IO server error:', data);
      const errorType = data.type as SocketErrorType;
      
      // Handle connection_replaced silently (normal behavior when new connection established)
      if (errorType === 'connection_replaced') {
        // Normal behavior - no logging needed
        return; // Don't show error to user
      }
      
      // Handle rate limit errors
      if (errorType === 'rate_limit') {
        // Rate limit - notify user with wait time, message should be queued
        const retryAfterSeconds = data.retryAfter || 60;
        latestOnErrorRef.current?.(`Rate limit exceeded. Please wait ${retryAfterSeconds} seconds before retrying.`, errorType);
        return;
      }
      
      // Handle non-retryable errors (don't retry)
      if (errorType === 'CONVERSATION_NOT_FOUND' || errorType === 'ACCESS_DENIED' || errorType === 'GUEST_CONVERSATION_MISMATCH') {
        const currentId = latestConversationIdRef.current;
        if (errorType === 'CONVERSATION_NOT_FOUND' && currentId) {
          latestOnConversationNotFoundRef.current?.(currentId);
        }
        latestOnErrorRef.current?.(data.message || 'Access denied or conversation not found.', errorType);
        return;
      }
      
      // Handle access_denied and conversation_not_found (lowercase versions) - handled in joinConversation retry logic
      if (errorType === 'access_denied' || errorType === 'conversation_not_found') {
        const currentId = latestConversationIdRef.current;
        if (errorType === 'conversation_not_found' && currentId) {
          latestOnConversationNotFoundRef.current?.(currentId);
        }
        // Don't trigger generic error callback here to avoid duplicate error messages
        // These are handled in joinConversation retry logic
        // No logging needed - retry logic handles it
        return;
      }
      
      // Other server errors - show error but don't retry automatically
      latestOnErrorRef.current?.(data.message || 'A server error occurred.', errorType);
    });

    // Chat events from server
    socket.on('chat_event', (data: ChatWebSocketMessage): void => {
      try {
        // CRITICAL FIX #1: Filter events by conversationId to prevent cross-conversation pollution
        // If event has a conversationId and it doesn't match current, ignore it
        // This prevents typing indicators from old conversations appearing in new ones
        const currentId = latestConversationIdRef.current;
        if (data.conversationId && currentId && data.conversationId !== currentId) {
          return;
        }

        if (data.type === 'typing') {
          latestOnTypingRef.current?.();
          return;
        }

        if (data.type === 'chunk' && data.content !== undefined) {
          const onChunkFn = latestOnChunkRef.current;
          if (onChunkFn) {
            const chunk: ChatStreamChunk = {
              conversationId: data.conversationId || '',
              messageId: data.messageId || '',
              content: data.content,
              isComplete: data.isComplete || false,
              error: data.error,
              isOffTopic: data.isOffTopic,
              suggestions: data.suggestions
            };
            onChunkFn(chunk);
          }
          return;
        }

        if (data.type === 'complete') {
          latestOnCompleteRef.current?.(data.messageId || null, {
            isOffTopic: data.isOffTopic,
            suggestions: data.suggestions
          });
          return;
        }

        if (data.type === 'error' && data.error) {
          latestOnErrorRef.current?.(data.error, 'server_error');
          return;
        }

        if (data.type === 'stream_resume') {
          const onResumeFn = latestOnStreamResumeRef.current;
          if (onResumeFn) {
            const resumeData: StreamResumeData = {
              conversationId: data.conversationId || '',
              messageId: data.messageId || null,
              bufferedContent: data.bufferedContent || '',
              chunkCount: data.chunkCount || 0,
              startedAt: data.startedAt || Date.now(),
              isComplete: data.isComplete || false,
              isError: data.isError || false,
              errorMessage: data.errorMessage,
              isOffTopic: data.isOffTopic,
              suggestions: data.suggestions
            };
            onResumeFn(resumeData);
          }
          return;
        }
      } catch (parseError) {
        console.error('Failed to process chat_event:', parseError);
        latestOnErrorRef.current?.('Failed to process server message', 'parse_error');
      }
    });
    
    eventHandlersRegisteredRef.current = true;
  }, [onOpen, onClose, processPendingOperations]);


  /**
   * Connect to Socket.IO server (singleton pattern with 2025 best practices)
   * - Auto-reconnect with exponential backoff (unlimited attempts)
   - Internet-aware (pauses when offline, resumes when online)
   - Pre-send connection check (triggers reconnect if needed)
   */
  const connect = useCallback((): void => {
    if (!enabled) {
      // If disabled, disconnect existing connection
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        joinedConversationsRef.current.clear();
        eventHandlersRegisteredRef.current = false;
        setIsConnected(false);
        setIsConnecting(false);
      }
      return;
    }

    // Don't attempt connection if offline
    if (!isOnlineRef.current) {
      setIsConnecting(false);
      return;
    }

    // Check if socket already exists and is connected
    if (socketRef.current && socketRef.current.connected) {
      // Check if access token changed - need to reconnect with new token
      if (lastAccessTokenRef.current !== accessToken) {
        // Access token changed - reconnect with new token
        socketRef.current.disconnect();
        socketRef.current = null;
        joinedConversationsRef.current.clear();
        eventHandlersRegisteredRef.current = false;
        setIsConnected(false);
      } else {
        // Socket exists and connected, and token hasn't changed - reuse it
        setIsConnected(true);
        setIsConnecting(false);
        return;
      }
    }

    // If socket exists but is disconnected, trigger reconnect
    if (socketRef.current && !socketRef.current.connected && accessToken === lastAccessTokenRef.current) {
      setIsConnecting(true);
      socketRef.current.connect();
      return;
    }

    try {
      const config = getApiConfig();
      const socketUrl = config.baseURL;

      setIsConnecting(true);

      const socket = io(socketUrl, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        auth: accessToken ? { token: accessToken } : undefined,
        query: accessToken ? { token: accessToken } : undefined,
        // 2025 best practices: unlimited reconnection attempts with exponential backoff
        reconnection: true,
        reconnectionDelay: RECONNECT_DELAY_MIN,
        reconnectionDelayMax: RECONNECT_DELAY_MAX,
        reconnectionAttempts: Infinity, // Keep trying in background
        timeout: 20000,
        // Exponential backoff: delay doubles each attempt
        randomizationFactor: 0.5, // Add some randomness to prevent thundering herd
      });

      registerEventHandlers(socket);
      socketRef.current = socket;
      lastAccessTokenRef.current = accessToken;
    } catch (error) {
      console.error('Failed to create Socket.IO connection:', error);
      setIsConnecting(false);
      setIsConnected(false);
      onError?.(error instanceof Error ? error.message : 'Connection failed', 'connection_error');
    }
  }, [enabled, accessToken, registerEventHandlers, onError]);

  /**
   * Clear retry state for a conversation
   */
  const clearRetryState = useCallback((conversationId: string): void => {
    joinRetryCountRef.current.delete(conversationId);
    const timeout = joinRetryTimeoutRef.current.get(conversationId);
    if (timeout !== undefined) {
      window.clearTimeout(timeout);
      joinRetryTimeoutRef.current.delete(conversationId);
    }
  }, []);

  /**
   * Wait for socket to be connected and stable
   * Returns true if connection is ready, false if timeout
   */
  const waitForConnection = useCallback((maxWaitMs: number = 5000): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!socketRef.current) {
        resolve(false);
        return;
      }
      
      if (socketRef.current.connected) {
        // Double-check connection is stable
        setTimeout(() => {
          resolve(socketRef.current?.connected === true && isMountedRef.current);
        }, 100);
        return;
      }
      
      let attempts = 0;
      const maxAttempts = Math.floor(maxWaitMs / 200); // Check every 200ms
      
      const checkConnection = (): void => {
        attempts++;
        if (attempts > maxAttempts || !isMountedRef.current) {
          resolve(false);
          return;
        }
        
        if (socketRef.current?.connected) {
          // Wait a bit more to ensure connection is stable
          setTimeout(() => {
            resolve(socketRef.current?.connected === true && isMountedRef.current);
          }, 150);
        } else {
          setTimeout(checkConnection, 200);
        }
      };
      
      checkConnection();
    });
  }, []);

  /**
   * Join a conversation room with 2025 best practices:
   * - Pre-send connection check (triggers reconnect if needed)
   * - Operation queue for disconnected state
   * - Retry logic with exponential backoff
   * - Connection stability verification before emitting
   */
  const joinConversation = useCallback((convId: string, guestId?: string | null): void => {
    // Check if component is still mounted
    if (!isMountedRef.current) return;
    
    // If already joined, don't join again
    if (joinedConversationsRef.current.has(convId)) {
      return;
    }
    
    // If already in active join operation, don't duplicate
    if (activeJoinOperationsRef.current.has(convId)) {
      return;
    }
    
    // 2025 best practice: Check connection before sending, trigger reconnect if needed
    if (!socketRef.current) {
      // No socket instance - create one
      connect();
      const currentId = latestConversationIdRef.current;
      if (currentId && convId !== currentId) {
        clearStalePendingOperations(currentId);
      }
      pendingOperationsRef.current.push({ type: 'join_conversation', conversationId: convId, guestId });
      return;
    }

    if (!socketRef.current.connected) {
      setIsConnecting(true);
      socketRef.current.connect();
      
      const currentId = latestConversationIdRef.current;
      if (currentId && convId !== currentId) {
        clearStalePendingOperations(currentId);
      }
      // Queue operation for when connection is established
      pendingOperationsRef.current.push({ type: 'join_conversation', conversationId: convId, guestId });
      
      // Wait for connection with promise-based approach (fire-and-forget)
      waitForConnection(10000).then((connected) => {
        if (connected && isMountedRef.current) {
          // Connection established - processPendingOperations will handle it
          processPendingOperations();
        }
      }).catch(() => {
        // Connection failed - operation is queued, will execute when connected
      });
      return;
    }

    // Clear any existing retry timeout for this conversation
    clearRetryState(convId);

    // Get current retry count
    const retryCount = joinRetryCountRef.current.get(convId) || 0;

    // Check if max retries reached
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      console.error(`Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached for conversation:`, convId);
      latestOnErrorRef.current?.(
        'Failed to join conversation after multiple attempts. The conversation may not exist or you may not have access.',
        'access_denied'
      );
      clearRetryState(convId);
      return;
    }

    // Set up error handler for this join attempt
    const errorHandler = (data: { type: string; message: string; retryAfter?: number }): void => {
      // Only handle errors for this specific conversation
      // Note: Socket.IO errors are global, so we need to check if this error is related to our join
      const errorType = data.type as StructuredSocketError['type'];

      // CRITICAL FIX #2: ACCESS_DENIED is a permanent failure - DO NOT RETRY
      // User doesn't own this conversation. Retrying wastes time and bandwidth.
      // Treat ACCESS_DENIED same as CONVERSATION_NOT_FOUND
      if (errorType === 'access_denied' || errorType === 'ACCESS_DENIED' ||
          errorType === 'conversation_not_found' || errorType === 'CONVERSATION_NOT_FOUND') {
        // Clear stale conversation ID - don't retry for permanent failures
        // CRITICAL: Also clear from pending operations queue to prevent retrying with stale ID
        clearStalePendingOperations(convId);
        latestOnConversationNotFoundRef.current?.(convId);
        clearRetryState(convId);
        activeJoinOperationsRef.current.delete(convId);
        socketRef.current?.off('error', errorHandler);

        if (errorType === 'access_denied' || errorType === 'ACCESS_DENIED') {
          latestOnErrorRef.current?.(
            'You do not have access to this conversation. Starting a new conversation...',
            errorType
          );
        }
        return;
      }
    };

    // Listen for errors (will be removed after handling or on success)
    socketRef.current.once('error', errorHandler);

    // Emit join request
    socketRef.current.emit('join_conversation', {
      conversationId: convId,
      ...(guestId !== undefined ? { guestId } : {})
    });

    // Set up success handler - if no error after 2 seconds, assume success
    window.setTimeout(() => {
      socketRef.current?.off('error', errorHandler);
      
      // If still not in joined set, add it (successful join)
      if (!joinedConversationsRef.current.has(convId)) {
        joinedConversationsRef.current.add(convId);
        clearRetryState(convId);
        // Successfully joined - no logging needed for normal operation
      }
    }, 2000);
  }, [clearRetryState, connect, processPendingOperations, waitForConnection, clearStalePendingOperations]);

  /**
   * Disconnect Socket.IO connection
   * Only disconnects if no active join operations to prevent interrupting critical operations
   */
  const disconnect = useCallback((): void => {
    // Don't disconnect if there are active join operations
    if (activeJoinOperationsRef.current.size > 0) {
      // Wait a bit and try again
      setTimeout(() => {
        if (activeJoinOperationsRef.current.size === 0) {
          disconnect();
        }
      }, 500);
      return;
    }
    
    // Clear all retry timeouts
    joinRetryTimeoutRef.current.forEach(timeout => window.clearTimeout(timeout));
    joinRetryTimeoutRef.current.clear();
    joinRetryCountRef.current.clear();
    activeJoinOperationsRef.current.clear();

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      joinedConversationsRef.current.clear();
      eventHandlersRegisteredRef.current = false;
    }
  }, []);

  /**
   * Internet-aware reconnection: pause when offline, resume when online
   */
  useEffect(() => {
    const handleOnline = (): void => {
      isOnlineRef.current = true;
      if (enabled && socketRef.current && !socketRef.current.connected) {
        // Resume reconnection when coming back online
        connect();
      }
    };

    const handleOffline = (): void => {
      isOnlineRef.current = false;
      setIsConnecting(false);
      // Pause reconnection attempts when offline
      // Socket.IO will automatically resume when online
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return (): void => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enabled, connect]);

  /**
   * Page Visibility API: Handle tab switching and ensure connection/rejoin on return
   * Fixes issue where messages are sent/received but UI doesn't update after tab switch
   * DEBOUNCED: Prevents excessive refreshes on rapid tab switches
   */
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = (): void => {
      if (!isMountedRef.current) return;

      // Clear any pending debounce timeout
      if (visibilityDebounceTimeoutRef.current !== null) {
        window.clearTimeout(visibilityDebounceTimeoutRef.current);
        visibilityDebounceTimeoutRef.current = null;
      }

      if (document.hidden) {
        // Tab is hidden - connection stays active but we pause activity
        // Socket.IO will handle reconnection automatically if needed
        // No logging needed for normal behavior
        return;
      }

      // Tab is visible again - debounce to prevent excessive refreshes
      visibilityDebounceTimeoutRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) return;

        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

        // Only refresh if it's been more than MIN_REFRESH_INTERVAL_MS since last refresh
        if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
          // Too soon since last refresh - skip this one
          return;
        }

        // Update last refresh time
        lastRefreshTimeRef.current = now;

        // Ensure connection is active and rejoin conversation if needed
        if (socketRef.current) {
          if (!socketRef.current.connected) {
            // Connection lost - reconnect
            setIsConnecting(true);
            socketRef.current.connect();
            
            // Wait for connection before rejoining
            const checkConnection = (): void => {
              if (socketRef.current?.connected && isMountedRef.current && currentConversationId) {
                if (!joinedConversationsRef.current.has(currentConversationId)) {
                  joinConversation(currentConversationId);
                } else {
                  // Already joined - only refresh if enough time has passed
                  onRejoinNeeded?.();
                }
              } else if (socketRef.current && !socketRef.current.connected && isMountedRef.current) {
                // Still connecting - check again in 500ms
                setTimeout(checkConnection, 500);
              }
            };
            setTimeout(checkConnection, 500);
          } else {
            // Connection exists - verify and rejoin conversation if needed
            if (currentConversationId && !joinedConversationsRef.current.has(currentConversationId)) {
              // Rejoin conversation after a brief delay to ensure connection is stable
              setTimeout(() => {
                if (socketRef.current?.connected && isMountedRef.current && currentConversationId) {
                  joinConversation(currentConversationId);
                }
              }, 200);
            } else if (currentConversationId && joinedConversationsRef.current.has(currentConversationId)) {
              // Already joined - only refresh state if enough time has passed
              onRejoinNeeded?.();
            }
          }
        } else if (enabled) {
          // No socket instance - create one
          connect();
        }
      }, 500); // 500ms debounce delay
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return (): void => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityDebounceTimeoutRef.current !== null) {
        window.clearTimeout(visibilityDebounceTimeoutRef.current);
        visibilityDebounceTimeoutRef.current = null;
      }
    };
  }, [enabled, currentConversationId, connect, joinConversation, onRejoinNeeded]);

  /**
   * Effect: Manage connection lifecycle
   * Only reconnect when enabled state or accessToken changes
   * Do NOT reconnect when conversationId changes - reuse same connection
   */
  useEffect(() => {
    isMountedRef.current = true;
    connect();

    return (): void => {
      // Only disconnect on unmount if explicitly disabled
      if (!enabled) {
        disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, accessToken]); // Intentionally exclude conversationId and other callbacks

  /**
   * Cleanup on unmount - use ref to prevent premature cleanup
   */
  useEffect(() => {
    isMountedRef.current = true;
    
    return (): void => {
      isMountedRef.current = false;
      // Small delay to allow any in-flight operations to complete
      setTimeout(() => {
        if (!isMountedRef.current) {
          disconnect();
        }
      }, 100);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount - use ref for disconnect

  return {
    joinConversation,
    disconnect,
    isConnected,
    isConnecting,
    reconnect: connect,
    clearStalePendingOperations,
    updatePendingOperationsConversationId
  };
};

